import LocationType from 'app/entities/registration/registration-steps/location-type';
import MotherEmergencyType from 'app/entities/registration/registration-steps/mother-emergency-type';
import {
  resetRegistration,
  saveRegistrationData,
  setCurrentStep,
  setRegistrationData
} from 'app/entities/registration/registration.reducer';
import {IRootState} from 'app/shared/reducers';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {RouteComponentProps, useHistory, useLocation} from 'react-router-dom';
import {Button, Col, Container, Row} from 'reactstrap';
import LoadingOverlay from 'react-loading-overlay';
import Sidebar from './sidebar';

import BabySymptoms from 'app/entities/registration/registration-steps/baby-symptoms';
import MotherSymptoms from 'app/entities/registration/registration-steps/mother-symptoms';
import PatientBabyInfo from 'app/entities/registration/registration-steps/patient-baby-info';
import PatientInfo from 'app/entities/registration/registration-steps/patient-info';
import PatientType from 'app/entities/registration/registration-steps/patient-type';
import ReasonForTransfer from 'app/entities/registration/registration-steps/reason-for-transfer';
import SelectVillage from 'app/entities/registration/registration-steps/select-village';
import SelectFacility from './registration-steps/select-facility';
import Bypass from './registration-steps/bypass';
import FacilitySuggestions from './registration-steps/facility-suggestions';
import {LocationTypeEnum} from 'app/entities/registration/registration-steps/location-type';
import {CaseType} from 'app/shared/model/enumerations/case-type.model';
import './registration.scss';
import {getMMamaSettings} from "app/modules/administration/mmama-settings/mmama-settings.reducer";
import {IMMamaSettings} from 'app/shared/model/mmama-settings-model';
import {getAllByType as getCountries} from 'app/entities/region/region.reducer';

interface IRegistrationProps extends StateProps, DispatchProps, RouteComponentProps {
}

interface IStepComponentProps {
  handlePreviousStep: () => void;
  handleNextStep: () => void;
  currentStep: number;
  onFinish?: () => void;
  updating?: boolean;
}

const Registration = (props: IRegistrationProps) => {
  const {currentStep, registrationData, updating, updatingSuccess, errorMessage, countries, mmamaSettings} = props;
  const history = useHistory();
  const location = useLocation();

  const [navigationStack, setNavigationStack] = useState<number[]>([]); // Initialize empty like followup modal
  const [countrySettings, setCountrySettings] = useState<IMMamaSettings | null>(null);

  // Reset registration when component mounts (new case)
  useEffect(() => {
    props.resetRegistration();
    return () => {
      // Reset registration when component unmounts (close modal)
      props.resetRegistration();
    };
  }, []);

  // Extract countryId from URL parameters and set it in registration data
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const countryId = urlParams.get('countryId');
    if (countryId) {
      props.setRegistrationData({countryId});
    }
  }, [location.search]);

  // Set initial step based on allowBypassCase and allowVillagePickup setting
  useEffect(() => {
    if (countrySettings) {
      // If allowBypassCase is false, skip step 1 (EmergencyType) and start from step 2 (LocationType)
      if (!countrySettings.allowBypassCase && currentStep === 1) {
        if (countrySettings.allowVillagePickup) {
          props.setCurrentStep(2);
        } else { // If allowVillagePickup is false, skip step 2 (LocationType) and start from step 3
          props.setRegistrationData({ locationType: LocationTypeEnum.HEALTH_FACILITY, villageId: null, village: null });
          props.setCurrentStep(3);
        }
      }
    }
  }, [countrySettings, currentStep]);

  // Fetch countries and mmamaSettings when component mounts
  useEffect(() => {
    props.getCountries();
    props.getMMamaSettings();
  }, []);

  // Set countrySettings when mmamaSettings and activeCountry are available
  useEffect(() => {
    if (mmamaSettings && mmamaSettings.length > 0 && registrationData.countryId) {
      const settings = mmamaSettings.find(setting => setting.country.id === registrationData.countryId);
      if (settings) {
        setCountrySettings(settings);
      }
    }
  }, [mmamaSettings, registrationData.countryId]);

  // Dynamic next step calculation
  const calculateNextStep = (currentStepParam: number): number => {
    const {locationType, patientCase} = registrationData;

    switch (currentStepParam) {
      case 1: // Bypass (conditional - only if allowBypassCase is true)
        if (countrySettings && !countrySettings.allowVillagePickup) {
          props.setRegistrationData({ locationType: LocationTypeEnum.HEALTH_FACILITY, villageId: null, village: null });
          return 3; // SelectFacility
        }
        return 2; // LocationType

      case 2: // LocationType
        if (locationType === LocationTypeEnum.HEALTH_FACILITY) {
          return 3; // SelectFacility
        } else if (locationType === LocationTypeEnum.COMMUNITY) {
          return 4; // SelectVillage
        }
        return currentStepParam + 1;

      case 3: // SelectFacility
        return 5; // PatientType

      case 4: // SelectVillage
        return 5; // PatientType

      case 5: // PatientType
        if (locationType === LocationTypeEnum.HEALTH_FACILITY && patientCase?.type !== CaseType.OTHER) {
          return 6; // ReasonForTransfer
        }
        return 7; // PatientInfo

      case 6: // ReasonForTransfer
        return 7; // PatientInfo

      case 7: // PatientInfo
        // if OTHER -> step 12 (FacilitySuggestions), else continue with symptoms
        if (patientCase?.type === CaseType.OTHER) {
          return 12; // FacilitySuggestions (final step for OTHER cases)
        } else if (patientCase?.type === CaseType.BABY || patientCase?.type === CaseType.BOTH) {
          return 8; // PatientBabyInfo
        } else {
          return 9; // MotherEmergencyType
        }

      case 8: // PatientBabyInfo
        // if CaseType.MOTHER -> step 9, if CaseType.BOTH -> step 10 (skip emergency type selection), else -> step 11
        if (patientCase?.type === CaseType.MOTHER) {
          return 9; // MotherEmergencyType
        } else if (patientCase?.type === CaseType.BOTH) {
          return 10; // MotherSymptoms (skip step 9 as EmergencyType is already set to POSTPARTUM)
        } else {
          return 11; // BabySymptoms
        }

      case 9: // MotherEmergencyType
        return 10; // MotherSymptoms

      case 10: // MotherSymptoms
        // if CaseType.BOTH -> step 11, else -> finish
        if (patientCase?.type === CaseType.BOTH) {
          return 11; // BabySymptoms
        }
        return 10; // Stay on same step (final step for mother-only cases)

      case 11: // BabySymptoms (final step)
        return 11; // Stay on same step

      case 12: // FacilitySuggestions (final step for OTHER cases)
        return 12; // Stay on same step

      default:
        return currentStepParam + 1;
    }
  };

  const handleNextStep = () => {
    const nextStep = calculateNextStep(currentStep);

    if (nextStep !== currentStep) {
      // Add current step to navigation stack
      setNavigationStack(prev => [...prev, currentStep]);
      props.setCurrentStep(nextStep);
    }
  };

  const handlePreviousStep = () => {
    if (navigationStack.length > 0) {
      // Pop the last step from navigation stack
      const newStack = [...navigationStack];
      const previousStep = newStack.pop();
      setNavigationStack(newStack);
      if (previousStep) {
        props.setCurrentStep(previousStep);
      }
    }
  };

  const handleCloseClick = () => {
    if (window.confirm('Are you sure you want to exit registration? Any unsaved data will be lost.')) {
      history.goBack();
    }
  };

  // reset registration on unmount
  React.useEffect(() => {
    return () => {
      props.resetRegistration();
    };
  }, []);

  React.useEffect(() => {
    if (updatingSuccess) {
      history.goBack();
    } else if (errorMessage) {
      alert(errorMessage);
    }
  }, [updatingSuccess]);

  const commonProps: IStepComponentProps = {
    handlePreviousStep,
    handleNextStep,
    currentStep,
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Bypass {...commonProps} />;
      case 2:
        return <LocationType {...commonProps} />;
      case 3:
        return <SelectFacility {...commonProps} countrySettings={countrySettings} />;
      case 4:
        return <SelectVillage {...commonProps} countrySettings={countrySettings} />;
      case 5:
        return <PatientType {...commonProps} />;
      case 6:
        return <ReasonForTransfer {...commonProps} />;
      case 7:
        return <PatientInfo {...commonProps} countrySettings={countrySettings} />;
      case 8:
        return <PatientBabyInfo {...commonProps} />;
      case 9:
        return <MotherEmergencyType {...commonProps} />;
      case 10:
        return <MotherSymptoms {...commonProps} onFinish={() => props.saveRegistrationData(registrationData)}
                               updating={updating}/>;
      case 11:
        return <BabySymptoms {...commonProps} onFinish={() => props.saveRegistrationData(registrationData)}
                             updating={updating}/>;
      case 12:
        return <FacilitySuggestions {...commonProps} onFinish={() => props.saveRegistrationData(registrationData)}
                                     updating={updating} countrySettings={countrySettings}/>;
      default:
        return null;
    }
  };

  const isLoading = !(countrySettings?.id && mmamaSettings && mmamaSettings.length > 0 && registrationData.countryId);

  return (
    <LoadingOverlay
      active={isLoading}
      spinner
      text="Loading..."
      styles={{
        overlay: (base) => ({
          ...base,
          position: 'fixed',
          top: '62px',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
        }),
        wrapper: (base) => ({
          ...base,
          width: '100%',
          height: 'calc(100vh - 62px)',
        }),
      }}
    >
      <Container fluid>
        <Row>
          {/* Sidebar */}
          <Col md="3" className="bg-light p-4">
            <Sidebar currentStep={currentStep}/>
          </Col>

          {/* Main Content */}
          <Col md="9" className="p-4 d-flex flex-column" style={{minHeight: '80vh'}}>
            <Row className="flex-grow-1">
              <Col md="12" className="h-100">
                <Button close onClick={handleCloseClick} className="top-0 end-0 m-3"/>
                {/* Cross button to go back */}
                {renderStep()}
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </LoadingOverlay>
  );
};

const mapStateToProps = (state: IRootState) => ({
  currentStep: state.registration.currentStep,
  registrationData: state.registration.entity,
  updating: state.registration.updating,
  updatingSuccess: state.registration.updatingSuccess,
  errorMessage: state.registration.errorMessage,
  countries: state.region.countries,
  mmamaSettings: state.mmamaSettings.settings,
});

const mapDispatchToProps = {
  setCurrentStep,
  saveRegistrationData,
  resetRegistration,
  setRegistrationData,
  getCountries,
  getMMamaSettings,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(Registration);
